import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft, Dices, LogIn, Plus } from 'lucide-react';
import {
  TIME_CONTROL_PRESETS,
  formatTimeControl,
  isValidRoomCode,
  normalizeRoomCode,
  ROOM_CODE_LENGTH,
  type Color,
  type TimeControl,
} from '../../shared/protocol.ts';
import { useDisplayName } from '../app/identity.ts';
import { usePreferences } from '../app/preferences.ts';
import { PlayerAvatar, PlayerName } from '../components/PlayerLook.tsx';
import { navigate } from '../app/router.ts';
import { BigTile } from '../components/BigTile.tsx';
import { ColorChoice } from '../components/ColorChoice.tsx';
import { Link } from '../components/Link.tsx';
import { Segmented } from '../components/Segmented.tsx';
import { randomName } from '../lib/names.ts';
import { ApiRequestError, createRoom, roomExists } from '../online/api.ts';
import type { MessageKey } from '../i18n/messages.ts';

const UNLIMITED = 'none';
const tcKey = (tc: TimeControl) => formatTimeControl(tc)!;

const API_ERRORS: Record<string, MessageKey> = {
  rate_limited: 'error.rateLimited',
  network: 'error.network',
};

type Step = 'choose' | 'create' | 'join';

/** Short steps that each fit on one screen: name + choice, then create or join. */
export function FriendPage() {
  const { t } = usePreferences();
  const [step, setStepState] = useState<Step>('choose');
  // Steps slide in once the child starts moving between them (not on first arrival).
  const [moved, setMoved] = useState(false);
  const setStep = (next: Step) => {
    setStepState(next);
    setMoved(true);
  };

  const back = (
    <button type="button" className="back-link" onClick={() => setStep('choose')}>
      <ArrowLeft size={22} strokeWidth={2.5} /> {t('common.back')}
    </button>
  );

  if (step === 'create') return <CreateStep key="create" back={back} />;
  if (step === 'join') return <JoinStep key="join" back={back} />;

  return (
    <div className={moved ? 'page-fill step' : 'page-fill'} key="choose">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
        <h1 className="page-title">{t('friend.title')}</h1>
      </div>
      <div className="lobby-step">
        <NameCard />
        <div className="mode-grid pop-in">
          <BigTile
            kind="friend"
            art="✨"
            title={t('friend.create.title')}
            desc={t('friend.create.desc')}
            go={t('home.go')}
            onClick={() => setStep('create')}
          />
          <BigTile
            kind="join"
            art="🔑"
            title={t('friend.join.title')}
            desc={t('friend.join.desc')}
            go={t('home.go')}
            onClick={() => setStep('join')}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Guests: the nickname box (random name, editable, dice for a new one).
 * Signed-in players: just show the account name they play under.
 */
function NameCard() {
  const { prefs, update, t } = usePreferences();
  const me = useDisplayName();
  if (me.signedIn) {
    return (
      <div className="card name-card signed-in">
        <span className="field-label">{t('friend.playingAs')}</span>
        <PlayerAvatar look={me.look} name={me.name} size="sm" />
        <PlayerName name={me.name} look={me.look} />
      </div>
    );
  }
  return (
    <div className="card name-card">
      <label className="field-label" htmlFor="player-name">
        {t('friend.name')}
      </label>
      <input
        id="player-name"
        className="input"
        maxLength={20}
        autoComplete="nickname"
        placeholder={t('friend.namePlaceholder')}
        value={prefs.playerName}
        onChange={(e) => update({ playerName: e.target.value })}
      />
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => update({ playerName: randomName(prefs.locale, Math.random, prefs.playerName) })}
        aria-label={t('friend.newName')}
        title={t('friend.newName')}
      >
        <Dices size={26} />
      </button>
    </div>
  );
}

function usePlayerName(): string {
  return useDisplayName().name;
}

function CreateStep({ back }: { back: ReactNode }) {
  const { t } = usePreferences();
  const name = usePlayerName();
  // Kids rarely want a ticking clock, so games start without one.
  const [time, setTime] = useState<string>(UNLIMITED);
  const [color, setColor] = useState<Color | 'random'>('random');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const timeControl = time === UNLIMITED ? null : (TIME_CONTROL_PRESETS.find((p) => tcKey(p) === time) ?? null);
      const room = await createRoom({ timeControl, color, name });
      navigate(`/r/${room.code}`);
    } catch (err) {
      const reason = err instanceof ApiRequestError ? err.reason : 'unavailable';
      setError(t(API_ERRORS[reason] ?? 'error.generic'));
      setCreating(false);
    }
  };

  return (
    <div className="page-fill step">
      <div className="page-top">
        {back}
        <h1 className="page-title">{t('friend.create.title')}</h1>
      </div>
      <div className="lobby-step">
        <form className="card lobby-card" onSubmit={onCreate}>
          <div className="field">
            <span className="field-label">{t('time.label')}</span>
            <Segmented<string>
              name="time"
              label={t('time.label')}
              wrap
              value={time}
              onChange={setTime}
              options={[
                { value: UNLIMITED, label: t('time.unlimited') },
                ...TIME_CONTROL_PRESETS.map((p) => ({
                  value: tcKey(p),
                  label: (
                    <>
                      {t('time.minutes', { n: p.initialMs / 60_000 })}
                      {p.incrementMs > 0 && <small>{t('time.incrementShort', { n: p.incrementMs / 1000 })}</small>}
                    </>
                  ),
                })),
              ]}
            />
          </div>

          <ColorChoice name="room-color" value={color} onChange={setColor} />

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn btn-green btn-lg btn-block" disabled={creating}>
            {creating ? <span className="spinner" style={{ width: 26, height: 26 }} /> : <Plus size={28} strokeWidth={3} />}
            {t('friend.create.button')}
          </button>
        </form>
      </div>
    </div>
  );
}

function JoinStep({ back }: { back: ReactNode }) {
  const { t } = usePreferences();
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onJoin = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizeRoomCode(code);
    if (!isValidRoomCode(normalized)) {
      setError(t('friend.join.invalid'));
      return;
    }
    setJoining(true);
    setError(null);
    try {
      if (await roomExists(normalized)) {
        navigate(`/r/${normalized}`);
        return;
      }
      setError(t('friend.join.notFound'));
    } catch {
      setError(t('error.network'));
    }
    setJoining(false);
  };

  return (
    <div className="page-fill step">
      <div className="page-top">
        {back}
        <h1 className="page-title">{t('friend.join.title')}</h1>
      </div>
      <div className="lobby-step">
        <form className="card lobby-card" onSubmit={onJoin} noValidate>
          <div className="field">
            <label className="field-label" htmlFor="room-code">
              {t('friend.join.desc')}
            </label>
            <input
              id="room-code"
              className="input input-code"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              autoFocus
              maxLength={ROOM_CODE_LENGTH + 2}
              placeholder={t('friend.join.placeholder')}
              value={code}
              aria-invalid={error ? true : undefined}
              onChange={(e) => {
                setCode(normalizeRoomCode(e.target.value).slice(0, ROOM_CODE_LENGTH));
                setError(null);
              }}
            />
          </div>

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn btn-blue btn-lg btn-block" disabled={joining || code.length === 0}>
            {joining ? <span className="spinner" style={{ width: 26, height: 26 }} /> : <LogIn size={26} />}
            {t('friend.join.button')}
          </button>
        </form>
      </div>
    </div>
  );
}
